import React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../utils/cn';
import {
  TagRadix,
  isAmbiguousTagValue,
  markTagValueAsHex,
  tagValueToDecimal,
} from '../utils/tagValueRadix';

export interface TagRadixSelectorProps {
  /** Raw value currently typed in the tag field. */
  value: string;
  /** Operator's answer, or null while unanswered. */
  radix: TagRadix | null;
  onChange: (radix: TagRadix) => void;
  /** Distinguishes the radio group from other instances on the same screen. */
  name: string;
  /** Show the "you must choose" message — set once a submit was attempted without an answer. */
  showError?: boolean;
  disabled?: boolean;
  darkMode?: boolean;
}

/**
 * Asks which base an all-digit tag value is in, and shows the card number each
 * answer resolves to so the operator can match it against what the reader
 * displayed. Renders nothing for values that already state their own base
 * (0x prefix, A-F letters, leading zero, Wiegand) — see utils/tagValueRadix.
 */
export const TagRadixSelector: React.FC<TagRadixSelectorProps> = ({
  value,
  radix,
  onChange,
  name,
  showError = false,
  disabled = false,
  darkMode = true,
}) => {
  if (!isAmbiguousTagValue(value)) return null;

  const trimmed = value.trim();
  const asDec = tagValueToDecimal(trimmed, 'dec');
  const asHex = tagValueToDecimal(trimmed, 'hex');
  const stored = markTagValueAsHex(trimmed);

  const panel = darkMode ? 'bg-amber-950/30 border-amber-900/60' : 'bg-amber-50 border-amber-200';
  const title = darkMode ? 'text-amber-300' : 'text-amber-800';
  const body = darkMode ? 'text-gray-300' : 'text-gray-700';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const code = darkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800';

  const option = (opt: TagRadix, label: string, resolved: number | null, hint: string) => (
    <label
      className={cn(
        'flex items-start gap-2 py-1 select-none',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
      )}
    >
      <input
        type="radio"
        name={name}
        value={opt}
        checked={radix === opt}
        onChange={() => onChange(opt)}
        disabled={disabled}
        className="mt-0.5 accent-amber-500"
      />
      <span className={cn('text-xs', body)}>
        <span className="font-medium">{label}</span>
        {resolved !== null && (
          <>
            {' — cartão '}
            <code className={cn('px-1 rounded font-mono', code)}>{resolved}</code>
          </>
        )}
        <span className={cn('block', muted)}>{hint}</span>
      </span>
    </label>
  );

  return (
    <div className={cn('mt-2 p-3 rounded-lg border', panel)} role="group" aria-label="Base do valor da tag">
      <p className={cn('text-xs font-medium', title)}>
        Este valor é decimal ou hexadecimal?
      </p>
      <p className={cn('mt-0.5 text-xs', muted)}>
        <code className={cn('px-1 rounded font-mono', code)}>{trimmed}</code> só tem dígitos, então não há como saber
        em qual base o leitor informou o cartão. As duas leituras são cartões diferentes — confira no leitor e escolha:
      </p>
      <div className="mt-2">
        {option('dec', 'Decimal', asDec, 'Salvo exatamente como foi digitado.')}
        {option('hex', 'Hexadecimal', asHex, `Salvo como ${stored} — o zero à esquerda é o que marca o valor como hex.`)}
      </div>
      {showError && !radix && (
        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="h-3.5 w-3.5" />
          Escolha decimal ou hexadecimal para continuar.
        </p>
      )}
    </div>
  );
};
